import type { ReactNode } from 'react'
import { useViewport } from '../../lib/use-viewport'

/**
 * T2 — Page-with-stat-tiles layout template.
 *
 * Composes: PageHead slot → responsive grid of stat Cards (2–4 cols) →
 * optional list body below.
 *
 * Used by: HR Insights "Numbers", QA Triage stats row, Manager Dashboard,
 * HOD Overview top.
 *
 * Stat tile grid is responsive:
 *   - desktop: `statCols` columns (2–4, default 4)
 *   - tablet:  min(statCols, 2)
 *   - mobile:  2 columns (stat cards are compact enough)
 *
 * AppShell is NOT included; template renders as child of an AppShell route.
 */

export interface PageWithStatTilesProps {
  /** PageHead component — required. */
  head: ReactNode
  /** Stat Card components — rendered in the responsive grid. */
  tiles: ReactNode
  /** Number of columns for the stat tile grid on desktop (2–4). Default 4. */
  statCols?: 2 | 3 | 4
  /** Optional banner above the head. */
  banner?: ReactNode
  /** Optional list body rendered below the stat tile grid. */
  children?: ReactNode
}

const TABLET_STAT_COLS = 2
const MOBILE_STAT_COLS = 2

export function PageWithStatTiles({
  head,
  tiles,
  statCols = 4,
  banner,
  children,
}: PageWithStatTilesProps): JSX.Element {
  const { isMobile, isTablet } = useViewport()
  const columns = isMobile
    ? MOBILE_STAT_COLS
    : isTablet
      ? Math.min(statCols, TABLET_STAT_COLS)
      : statCols

  return (
    <div
      data-template="page-with-stat-tiles"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        width: '100%',
      }}
    >
      {banner ? (
        <div data-slot="banner">{banner}</div>
      ) : null}

      <div data-slot="head">{head}</div>

      <div
        data-slot="tiles"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gap: 12,
          marginBottom: children ? 24 : 0,
        }}
      >
        {tiles}
      </div>

      {children ? (
        <div
          data-slot="list"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  )
}
